import React, { useEffect, useState } from "react";
import { Headphones, Check, X, RotateCcw, Trash2, Heart, Music2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * RequestsPanel — host-side view of listener song requests for THIS
 * station. Polls /api/radio/host/{slug}/requests every 8 s while open
 * so a fresh request appears without refresh.
 *
 * Each row shows the listener (avatar + name), the requested track,
 * an optional dedication / message, and host actions:
 *   • Played   → marks status=played (logs the request as honoured)
 *   • Dismiss  → status=dismissed (kept for history but hidden)
 *   • Reopen   → only on played/dismissed views
 *   • Delete   → hard delete (host or admin)
 */
export default function RequestsPanel({ slug, onPlayRequest }) {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("open"); // open | played | dismissed
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/radio/host/${slug}/requests`, { params: { status: tab, limit: 60 } });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, tab]);
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      load();
    }, 8000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [slug, tab]);

  const act = async (rid, action) => {
    try {
      await api.post(`/radio/host/${slug}/requests/${rid}/${action}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (rid) => {
    try {
      await api.delete(`/radio/host/${slug}/requests/${rid}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="radio-host-requests-panel" className="rounded-xl border border-white/10 bg-black/40 p-3 mb-4">
      <header className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Headphones className="w-4 h-4 text-rose-300 shrink-0" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">Listener Requests</h3>
          <span className="text-[10px] text-white/45">{rows.length}</span>
        </div>
        <div className="flex items-center gap-1">
          {["open", "played", "dismissed"].map((t) => (
            <button
              key={t}
              data-testid={`radio-host-requests-tab-${t}`}
              onClick={() => setTab(t)}
              className={`text-[9px] font-black uppercase tracking-wider px-2 py-1 rounded border transition ${
                tab === t
                  ? "bg-rose-500/30 text-rose-100 border-rose-400/60"
                  : "bg-white/5 text-white/55 border-white/10 hover:bg-white/10"
              }`}
            >{t}</button>
          ))}
        </div>
      </header>

      {loading && rows.length === 0 ? (
        <p className="text-[11px] text-white/40 italic">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-[11px] text-white/40 italic">
          {tab === "open"
            ? "No open requests yet — share your /radio/station link to invite listeners."
            : `No ${tab} requests in the last 30 days.`}
        </p>
      ) : (
        <ul className="space-y-2 max-h-[340px] overflow-y-auto">
          {rows.map((r) => (
            <li
              key={r.id}
              data-testid={`radio-host-request-${r.id}`}
              className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-lg p-2"
            >
              {r.user_avatar ? (
                <img src={r.user_avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-rose-500/30 text-rose-100 grid place-items-center text-[10px] font-black shrink-0">
                  {(r.user_name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-[10px] text-white/55 font-bold">
                  <span className="truncate">{r.user_name || "Anon"}</span>
                  {r.dedicate_to && (
                    <span className="inline-flex items-center gap-0.5 text-pink-300">
                      <Heart className="w-3 h-3" /> for {r.dedicate_to}
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-white font-semibold truncate">
                  <Music2 className="w-3 h-3 inline-block mr-1 text-tv-accent" />
                  {r.track_title}{r.artist ? ` · ${r.artist}` : ""}
                </div>
                {r.message && (
                  <div className="text-[11px] text-white/65 italic mt-0.5">"{r.message}"</div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {tab === "open" ? (
                  <>
                    <button
                      data-testid={`radio-host-request-${r.id}-play`}
                      onClick={() => { onPlayRequest?.(r); act(r.id, "played"); }}
                      title="Mark as played"
                      className="p-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-100"
                    ><Check className="w-3.5 h-3.5" /></button>
                    <button
                      data-testid={`radio-host-request-${r.id}-dismiss`}
                      onClick={() => act(r.id, "dismissed")}
                      title="Dismiss"
                      className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-white/60"
                    ><X className="w-3.5 h-3.5" /></button>
                  </>
                ) : (
                  <button
                    data-testid={`radio-host-request-${r.id}-reopen`}
                    onClick={() => act(r.id, "reopen")}
                    title="Re-open"
                    className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-white/60"
                  ><RotateCcw className="w-3.5 h-3.5" /></button>
                )}
                <button
                  data-testid={`radio-host-request-${r.id}-delete`}
                  onClick={() => del(r.id)}
                  title="Delete"
                  className="p-1.5 rounded text-white/40 hover:text-rose-300"
                ><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
